/**
 * GalleryAdmin — CRUD for photo albums. Supports both kinds:
 *  - iCloud: paste the shared album URL, users open it in a new tab
 *  - Uploaded: photos are uploaded via multipart and stored in object storage
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COMMUNITIES = [
  { slug: 'nuts', label: '🥜 Nuts' },
  { slug: 'caps', label: '🧢 Caps' },
  { slug: 'shark', label: '🦈 Shark' },
  { slug: 'quinta', label: '🍺 Quinta' },
];

const emptyAlbum = () => ({
  title: { ca: '', es: '', en: '', fr: '' },
  description: { ca: '', es: '', en: '', fr: '' },
  communities: [],
  kind: 'uploaded',
  icloud_url: '',
  cover_image: '',
  visible: true,
  order: 0,
});

export const GalleryAdmin = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/zona-pro/gallery/albums`, authHeader());
      setAlbums(r.data?.albums || []);
    } catch {
      toast.error('No s\'han pogut carregar els àlbums');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/zona-pro/gallery/albums/${id}`, authHeader());
      setSelectedAlbum(r.data);
    } catch { toast.error('Error'); }
  };

  const save = async (a) => {
    // Basic validation
    if (!a.title?.ca?.trim() && !a.title?.es?.trim()) {
      toast.error('Cal com a mínim un títol (CA o ES)');
      return;
    }
    if (a.kind === 'icloud' && !a.icloud_url?.trim()) {
      toast.error('URL d\'iCloud obligatòria per aquest tipus d\'àlbum');
      return;
    }
    if ((a.communities || []).length === 0) {
      toast.error('Selecciona almenys una comunitat');
      return;
    }
    try {
      if (a.id || editingId) {
        const id = a.id || editingId;
        await axios.put(`${API_URL}/api/zona-pro/gallery/albums/${id}`, a, authHeader());
      } else {
        await axios.post(`${API_URL}/api/zona-pro/gallery/albums`, a, authHeader());
      }
      toast.success('Guardat');
      setDraft(null);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminar aquest àlbum? Es perdran totes les fotos.')) return;
    try {
      await axios.delete(`${API_URL}/api/zona-pro/gallery/albums/${id}`, authHeader());
      toast.success('Eliminat');
      await load();
      if (selectedAlbum?.id === id) setSelectedAlbum(null);
    } catch { toast.error('Error'); }
  };

  const uploadPhotos = async (files) => {
    if (!selectedAlbum || !files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    try {
      const r = await axios.post(
        `${API_URL}/api/zona-pro/gallery/albums/${selectedAlbum.id}/photos`,
        fd,
        { headers: { ...authHeader().headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`${r.data.count} foto(s) pujades`);
      await openDetail(selectedAlbum.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error pujant fotos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deletePhoto = async (photoId) => {
    if (!selectedAlbum || !window.confirm('Eliminar aquesta foto?')) return;
    try {
      await axios.delete(
        `${API_URL}/api/zona-pro/gallery/albums/${selectedAlbum.id}/photos/${photoId}`,
        authHeader(),
      );
      await openDetail(selectedAlbum.id);
      await load();
    } catch { toast.error('Error'); }
  };

  const toggleCommunity = (a, slug) => {
    const set = new Set(a.communities || []);
    if (set.has(slug)) set.delete(slug); else set.add(slug);
    return { ...a, communities: Array.from(set) };
  };

  const withApi = (u) => (u && u.startsWith('/api/') ? `${API_URL}${u}` : u);

  return (
    <div className="space-y-4" data-testid="gallery-admin">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-slate-600 flex-1">
          Crea àlbums de fotos per cada comunitat. Pot ser un àlbum compartit d&apos;iCloud (només URL)
          o un àlbum amb pujada directa (fins ~50 fotos per lot).
        </p>
        <Button
          onClick={() => { setDraft(emptyAlbum()); setEditingId(null); }}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="gallery-new-btn"
        >
          + Nou àlbum
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-8 text-center text-slate-500">Carregant…</div>
      ) : albums.length === 0 && !draft ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Encara no hi ha àlbums.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {albums.map((a) => (
            <Card key={a.id} data-testid={`gallery-row-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  {a.cover_image ? (
                    <img src={withApi(a.cover_image)} alt="" className="w-20 h-20 object-cover bg-slate-100 rounded" onError={(e) => { e.target.style.opacity = 0.3; }} />
                  ) : (
                    <div className="w-20 h-20 bg-slate-100 rounded flex items-center justify-center text-3xl">📷</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{a.title?.ca || a.title?.es || 'Sense títol'}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {a.kind === 'icloud' ? '☁️ iCloud' : `📷 ${a.photo_count || 0} fotos`}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {(a.communities || []).map((c) => c.charAt(0).toUpperCase() + c.slice(1)).join(', ')}
                    </div>
                    <div className="text-[11px] mt-1">{a.visible ? '👁️ Públic' : '🔒 Privat'}</div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  {a.kind === 'uploaded' && (
                    <Button size="sm" variant="outline" onClick={() => openDetail(a.id)} data-testid={`gallery-photos-${a.id}`}>
                      Fotos ({a.photo_count || 0})
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setDraft({ ...a, description: a.description || { ca: '', es: '', en: '', fr: '' } }); setEditingId(a.id); }} data-testid={`gallery-edit-${a.id}`}>
                    Editar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(a.id)} data-testid={`gallery-del-${a.id}`}>
                    Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Draft form */}
      {draft && (
        <Card className="border-emerald-300 mt-4" data-testid="gallery-draft-card">
          <CardContent className="p-4 space-y-3">
            <h4 className="font-medium">{editingId ? 'Editar àlbum' : 'Nou àlbum'}</h4>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipus</Label>
                <select
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={draft.kind}
                  onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                  data-testid="gallery-draft-kind"
                >
                  <option value="uploaded">📷 Pujada directa</option>
                  <option value="icloud">☁️ Enllaç iCloud</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Ordre</Label>
                <Input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value) || 0 })} />
              </div>

              {['ca', 'es', 'en', 'fr'].map((lg) => (
                <div key={lg} className="col-span-2">
                  <Label className="text-xs">Títol ({lg.toUpperCase()}){lg === 'ca' ? ' *' : ''}</Label>
                  <Input
                    value={draft.title[lg] || ''}
                    onChange={(e) => setDraft({ ...draft, title: { ...draft.title, [lg]: e.target.value } })}
                    data-testid={`gallery-draft-title-${lg}`}
                  />
                </div>
              ))}

              <div className="col-span-2">
                <Label className="text-xs">Descripció (CA)</Label>
                <Textarea rows={2} value={draft.description?.ca || ''} onChange={(e) => setDraft({ ...draft, description: { ...(draft.description || {}), ca: e.target.value } })} />
              </div>

              {draft.kind === 'icloud' && (
                <div className="col-span-2">
                  <Label className="text-xs">URL de l&apos;àlbum iCloud *</Label>
                  <Input
                    value={draft.icloud_url || ''}
                    onChange={(e) => setDraft({ ...draft, icloud_url: e.target.value })}
                    placeholder="https://www.icloud.com/sharedalbum/#..."
                    data-testid="gallery-draft-icloud-url"
                  />
                </div>
              )}

              <div className="col-span-2">
                <Label className="text-xs">Imatge de portada (URL) — opcional per uploaded</Label>
                <Input
                  value={draft.cover_image || ''}
                  onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })}
                  placeholder="https://…"
                  data-testid="gallery-draft-cover"
                />
              </div>

              <div className="col-span-2">
                <Label className="text-xs">Comunitats *</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {COMMUNITIES.map((c) => (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => setDraft((d) => toggleCommunity(d, c.slug))}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                        (draft.communities || []).includes(c.slug)
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'
                      }`}
                      data-testid={`gallery-draft-community-${c.slug}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-end gap-2 col-span-2">
                <input
                  id="gal-visible"
                  type="checkbox"
                  checked={draft.visible}
                  onChange={(e) => setDraft({ ...draft, visible: e.target.checked })}
                  data-testid="gallery-draft-visible"
                />
                <Label htmlFor="gal-visible" className="cursor-pointer">Àlbum visible al públic (desmarca per fer-lo privat)</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setDraft(null); setEditingId(null); }} data-testid="gallery-cancel-btn">Cancel·lar</Button>
              <Button onClick={() => save(draft)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gallery-save-btn">Guardar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail: photo management */}
      {selectedAlbum && (
        <Card className="border-blue-300 mt-4" data-testid="gallery-detail-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">📷 {selectedAlbum.title?.ca || selectedAlbum.title?.es}</h4>
              <Button size="sm" variant="outline" onClick={() => setSelectedAlbum(null)}>Tancar</Button>
            </div>

            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => uploadPhotos(e.target.files)}
                className="text-sm"
                data-testid="gallery-photo-upload"
              />
              {uploading && <span className="text-sm text-slate-500">Pujant…</span>}
            </div>

            {(selectedAlbum.photos || []).length === 0 ? (
              <p className="text-sm text-slate-500 py-4">Encara no hi ha fotos.</p>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2">
                {selectedAlbum.photos.map((p) => (
                  <div key={p.id} className="relative aspect-square bg-slate-100 rounded overflow-hidden group">
                    <img src={withApi(p.url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => deletePhoto(p.id)}
                      className="absolute top-1 right-1 bg-red-600 text-white w-6 h-6 rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Delete photo"
                      data-testid={`gallery-photo-del-${p.id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GalleryAdmin;
